import * as React from 'react';

import { useDispatch } from 'react-redux';

import { Divider, Grid, Spacer, Typography } from '@emburse/ui-core';

import { LoadingSkeleton } from '@ReactComponents/Shared/LoadingSkeleton';
import { StripeCheckoutSection } from '@ReactComponents/AdminSettings/Components/Billing/StripeCheckoutSection';

import { StoreCompany } from '@Actions/company';
import { useActivationPlanPricing } from '@ReactHooks/useActivationPlanPricing';
import { useStripeSubscriptionAndCheckout } from '@ReactHooks/useStripeSubscriptionAndCheckout';

import { BillApi } from '@ClientApi/billApi';
import { CompanyApi } from '@ClientApi/company';
import { BILLING_PLAN_TO_DESCRIPTION_MAPPING } from '@Shared/constants';
import { AbacusCurrency } from '@Shared/helpers';
import { StripeActivationPlanPricingDetails } from '@Shared/interfaces/BillingTypes';
import { ICompanyModel } from '@Shared/models';

import { SignUpWorkflowFooter } from '../SignUpWorkflowFooter';

import * as style from './SignUpPaymentSummary.styl';

interface ISignUpPaymentSummaryProps {
  company: ICompanyModel;
  emburseCards: boolean;
  onSummaryConfirmation: () => void;
  onError: () => void;
  isMigration?: boolean;
  selectedPlanId?: string;
  headerText?: string;
  containerStyleProps?: any;
  incompleteInvoiceAmount?: number;
}

interface SelectedPlanCopy {
  value: string;
  caption: string;
  name: string;
  description: string;
  cancellationPolicy: string;
}

const _buildSelectedPlanCopy = (
  billingPlan: string,
  pricingDetails: StripeActivationPlanPricingDetails | null,
  currencyCode: string,
  skipTrial?: boolean
): SelectedPlanCopy => {
  const selectedPlan: SelectedPlanCopy = {
    value: '',
    caption: '',
    name: BILLING_PLAN_TO_DESCRIPTION_MAPPING[billingPlan],
    description: '',
    cancellationPolicy: ''
  };

  if (!pricingDetails) {
    return selectedPlan;
  }

  const perUserAmount = pricingDetails.additionalUserAmountInCents;
  const includedUsers = pricingDetails.includedUsers;
  const minimumMonthly = pricingDetails.minimumMonthlyAmountInCents;

  switch (billingPlan) {
    case 'spend_basic_eight_dollars':
    case 'spend_plus_twelve_dollars': {
      selectedPlan.value = perUserAmount != null ? new AbacusCurrency(perUserAmount, currencyCode).toFormattedString() : '';
      selectedPlan.caption = '/user per month';
      const minimumDesc =
        includedUsers != null && minimumMonthly != null
          ? `${includedUsers} minimum user fee of ${new AbacusCurrency(minimumMonthly, currencyCode).toFormattedString()}/month`
          : '';
      selectedPlan.description = skipTrial || !minimumDesc ? minimumDesc : '30 days free then the ' + minimumDesc;
      selectedPlan.cancellationPolicy = skipTrial ? '' : 'No cancellation fee during trial period';
      break;
    }
    case 'amex_starter_free_stripe':
      selectedPlan.value = 'Free';
      selectedPlan.caption = '/unlimited users';
      selectedPlan.cancellationPolicy = 'No cancellation fee';
      break;
    case 'amex_professional_six_dollars_stripe':
      selectedPlan.value = perUserAmount != null ? new AbacusCurrency(perUserAmount, currencyCode).toFormattedString() : '';
      selectedPlan.caption = '/user';
      selectedPlan.description = skipTrial ? '' : '30 days free';
      selectedPlan.cancellationPolicy = skipTrial ? '' : 'No cancellation fee during trial period';
      break;
  }

  return selectedPlan;
};

export const SignUpPaymentSummary: React.FC<ISignUpPaymentSummaryProps> = ({
  company,
  emburseCards,
  onSummaryConfirmation,
  onError,
  isMigration = false,
  selectedPlanId,
  headerText = 'Review and Pay',
  containerStyleProps = {},
  incompleteInvoiceAmount = 0
}) => {
  const dispatch = useDispatch();
  const [estimatedImplementationTaxes, setEstimatedImplementationTaxes] = React.useState<number | null>(null);

  const billingPlan = selectedPlanId ?? company.billing_plan;

  const { pricingDetails, pricingLoading } = useActivationPlanPricing(billingPlan);

  const {
    stripeCheckoutSession,
    activeStripeSubscription,
    processingPaymentInfo,
    setStripeCheckoutSession,
    cancelStripeCheckoutSession,
    onUpdateStripePaymentMethod
  } = useStripeSubscriptionAndCheckout(company, onError);

  async function onPaymentSuccess() {
    if (company.state === 'pending') {
      const updatedCompany = await CompanyApi.activateCompany(company.id);
      const updatedCompanyWithNewState = { ...company, state: updatedCompany.state };
      dispatch(new StoreCompany(updatedCompanyWithNewState));
      window.ORG = { ...company, state: updatedCompany.state };
    }
  }

  async function onUpdatePaymentMethod(paymentMethodId: string) {
    const res = await onUpdateStripePaymentMethod(paymentMethodId);
    if (res?.subscription) {
      onSummaryConfirmation();
    }
  }

  const memoizedOnPaymentSuccess = React.useCallback(onPaymentSuccess, [company.id, company.state]);

  const implementationCost = company.implementation_options === 'self_guided' || isMigration ? 0 : 150000;
  React.useEffect(() => {
    async function getEstimatedTaxes() {
      if (implementationCost) {
        const { implementationTax } = await BillApi.getEstimatedTax(0, implementationCost);
        setEstimatedImplementationTaxes(implementationTax);
      } else {
        setEstimatedImplementationTaxes(0);
      }
    }

    getEstimatedTaxes();
  }, [company]);

  const selectedPlan = _buildSelectedPlanCopy(billingPlan, pricingDetails, company.currency_code, isMigration);
  const implementationType = company.implementation_options === 'self_guided' ? 'Self Guided Implementation' : 'Guided Implementation';
  const amountDueToday = incompleteInvoiceAmount > 0 ? incompleteInvoiceAmount : implementationCost + (estimatedImplementationTaxes || 0);

  if (pricingLoading) {
    return <LoadingSkeleton className={style.global} containerStyleProps={containerStyleProps} marginTop="120px" />;
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ height: '100%', maxWidth: '1270px' }} wrap="nowrap" className={style.global}>
      <Grid
        item
        container
        direction="row"
        style={{ marginTop: '120px', paddingLeft: '32px', paddingRight: '32px', minWidth: '1090px', ...containerStyleProps }}
      >
        <Grid item container direction="column" className="jc-c" xs={7} style={{ paddingRight: '64px' }}>
          <Typography variant="h4" style={{ alignSelf: 'flex-start' }}>
            {headerText}
          </Typography>
          <Spacer size={32} />
          <Grid container wrap="nowrap" direction="column" className="flex jc-c" style={{ rowGap: 24 }}>
            <Grid item>
              <div className="cardStyle">
                <div className="flex jc-sb">
                  <div>
                    <Typography variant="subtitle1" className="flex ai-c">
                      {selectedPlan.name}
                    </Typography>
                    <Spacer size={8} />
                    {selectedPlan.description && (
                      <>
                        <Typography variant="caption">{selectedPlan.description}</Typography>
                        <Spacer size={8} />
                      </>
                    )}
                    <Typography variant="caption">{selectedPlan.cancellationPolicy}</Typography>
                  </div>
                  <div className="flex fd-c ai-fe">
                    <Typography variant="h5" className="flex ai-c" data-testid="billing_plan_value">
                      {selectedPlan.value}
                    </Typography>
                    <Typography variant="body1" data-testid="billing_plan_caption">
                      {selectedPlan.caption}
                    </Typography>
                  </div>
                </div>
              </div>
            </Grid>

            <Divider style={{ borderColor: 'var(--grey300)' }} />

            {!isMigration && (
              <Grid item>
                <div className="cardStyle">
                  <div className="flex jc-sb">
                    <Typography variant="subtitle1" className="flex ai-c">
                      {implementationType}
                    </Typography>
                    <Typography variant="h5" className="flex ai-c">
                      {implementationCost === 0
                        ? 'Included'
                        : new AbacusCurrency(implementationCost, company.currency_code).toFormattedString(true)}
                    </Typography>
                  </div>
                  <Spacer size={8} />
                  <div style={{ maxWidth: '60%' }}>
                    {company.implementation_options === 'self_guided' ? (
                      <Typography variant="caption">
                        Leverage in-product guides, live and recorded webinars, video and written content, and more to easily self-implement
                        your Emburse Spend account and get started!
                      </Typography>
                    ) : (
                      <Typography variant="caption">
                        30 day, 1:1 guided implementation project; includes configuration support, best practices, training, and more with
                        an Emburse Spend expert resource
                      </Typography>
                    )}
                  </div>
                </div>
              </Grid>
            )}

            {!isMigration && <Divider style={{ borderColor: 'var(--grey300)' }} />}

            {emburseCards && (
              <>
                <Grid item>
                  <div className="cardStyle">
                    <div className="flex jc-sb">
                      <Typography variant="subtitle1" className="flex ai-c">
                        Emburse Cards
                      </Typography>
                      <Typography variant="h5" className="flex ai-c">
                        Free
                      </Typography>
                    </div>
                    <Spacer size={8} />
                    <div style={{ maxWidth: '60%' }}>
                      <Typography variant="caption">
                        Limit not only how much employees can spend, but when and where purchases can be made. Which provides greater
                        oversight and control to your daily processing
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Divider style={{ borderColor: 'var(--grey300)' }} />
              </>
            )}

            <Grid item>
              <div className="total-card">
                <div className="flex jc-sb">
                  <Typography variant="subtitle1" className="flex">
                    Due Today
                  </Typography>
                  <div className="flex fd-c ai-fe">
                    <Typography variant="h5" className="flex ai-c" data-testid="cost-due">
                      {estimatedImplementationTaxes !== null &&
                        new AbacusCurrency(amountDueToday, company.currency_code).toFormattedString(Boolean(amountDueToday))}
                    </Typography>
                    {estimatedImplementationTaxes !== null && estimatedImplementationTaxes > 0 && (
                      <Typography variant="body1" data-testid="cost-due-caption">
                        includes sales taxes of{' '}
                        {new AbacusCurrency(estimatedImplementationTaxes, company.currency_code).toFormattedString()}
                      </Typography>
                    )}
                  </div>
                </div>
                {implementationCost === 0 && (
                  <>
                    <Spacer size={24} />
                    <Typography variant="caption">Note: Applicable taxes will be calculated and added to your monthly bill.</Typography>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container xs={5}>
          <StripeCheckoutSection
            stripeCheckoutSession={stripeCheckoutSession}
            activeStripeSubscription={activeStripeSubscription}
            processingPaymentInfo={processingPaymentInfo}
            onUpdatePaymentMethod={onUpdatePaymentMethod}
            setStripeCheckoutSession={setStripeCheckoutSession}
            cancelStripeCheckoutSession={cancelStripeCheckoutSession}
            showSubscribe={true}
            onCancel={() => {}}
            onPaymentSuccess={memoizedOnPaymentSuccess}
          />
        </Grid>
      </Grid>

      <Grid item xs>
        <Spacer size={4} />
      </Grid>

      <Grid item container xs alignContent="flex-end">
        <SignUpWorkflowFooter onNext={() => onSummaryConfirmation()} hideButtons showSupportLink ariaLabel="payment-summary" />
      </Grid>
    </Grid>
  );
};
